var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
import { createJsonCommandExtractor, jsonParser } from './framework/JsonCommandExtractor';
import { Timeouts } from './framework/Timeouts';
const cameraDirectionMap = {
    down: { axis: 'Tilt', direction: 'Down' },
    in: { axis: 'Zoom', direction: 'In' },
    left: { axis: 'Pan', direction: 'Left' },
    right: { axis: 'Pan', direction: 'Right' },
    out: { axis: 'Zoom', direction: 'Out' },
    up: { axis: 'Tilt', direction: 'Up' },
};
const ciscoCallAcceptResp = z.object({
    ResultId: z.string().optional(),
    CommandResponse: z
        .object({
        CallAcceptResult: z
            .object({
            status: z.string().optional(),
        })
            .optional(),
    })
        .optional(),
});
const ciscoCallAsyncResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Call: z.array(z.object({
            id: z.string(),
            ghost: z.string().optional(),
            CallType: z.object({ Value: z.string() }).optional(),
            Direction: z.object({ Value: z.string() }).optional(),
            DisplayName: z.object({ Value: z.string() }).optional(),
            Duration: z.object({ Value: z.string() }).optional(),
            CallbackNumber: z.object({ Value: z.string() }).optional(),
            RemoteNumber: z.object({ Value: z.string() }).optional(),
            Status: z.object({ Value: z.string() }).optional(),
        })),
    }),
});
const ciscoCallDisconnectResp = z.object({
    ResultId: z.string().optional(),
    CommandResponse: z
        .object({
        CallDisconnectResult: z
            .object({
            status: z.string().optional(),
        })
            .optional(),
    })
        .optional(),
});
const ciscoCallRejectResp = z.object({
    ResultId: z.string().optional(),
    CommandResponse: z
        .object({
        CallRejectResult: z
            .object({
            status: z.string().optional(),
        })
            .optional(),
    })
        .optional(),
});
const ciscoCallQueryResp = z.object({
    ResultId: z.string(),
    Status: z
        .object({
        Call: z
            .array(z.object({
            id: z.string(),
            ghost: z.string().optional(),
            CallType: z.object({ Value: z.string() }).optional(),
            Direction: z.object({ Value: z.string() }).optional(),
            DisplayName: z.object({ Value: z.string() }).optional(),
            Duration: z.object({ Value: z.string() }).optional(),
            CallbackNumber: z.object({ Value: z.string() }).optional(),
            RemoteNumber: z.object({ Value: z.string() }).optional(),
            Status: z.object({ Value: z.string() }).optional(),
        }))
            .optional(),
    })
        .optional(),
});
const ciscoCamerasResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Cameras: z.object({
            Camera: z.array(z.object({
                id: z.string(),
                Capabilities: z.object({
                    Options: z.object({
                        Value: z.string(),
                    }),
                }),
                DetectedConnector: z.object({
                    Value: z.string(),
                }),
            })),
        }),
    }),
});
const ciscoDialResp = z.object({
    ResultId: z.string().optional(),
    CommandResponse: z
        .object({
        DialResult: z
            .object({
            CallId: z
                .object({
                Value: z.string().optional(),
            })
                .optional(),
            ConferenceId: z
                .object({
                Value: z.string().optional(),
            })
                .optional(),
            status: z.string().optional(),
        })
            .optional(),
    })
        .optional(),
});
const ciscoGenericResp = z.object({
    ResultId: z.string().optional(),
});
const ciscoGpioStateResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        GPIO: z.object({
            Pin: z.array(z.object({
                State: z.object({
                    Value: z.string(),
                }),
                id: z.string(),
            })),
        }),
    }),
});
const ciscoMaxAudioCallsResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Capabilities: z
            .object({
            Conference: z.object({
                MaxAudioCalls: z.object({
                    Value: z.string(),
                }),
            }),
        })
            .optional(),
    }),
});
const ciscoMaxVideoCallsResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Capabilities: z
            .object({
            Conference: z.object({
                MaxVideoCalls: z.object({
                    Value: z.string(),
                }),
            }),
        })
            .optional(),
    }),
});
const ciscoMicrophoneMuteResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Audio: z.object({
            Microphones: z.object({
                Mute: z.object({
                    Value: z.string(),
                }),
            }),
        }),
    }),
});
const ciscoPresentationLocalInstanceResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Conference: z.object({
            Presentation: z.object({
                LocalInstance: z.array(z.object({
                    Source: z
                        .object({
                        Value: z.string(),
                    })
                        .optional(),
                    id: z.string(),
                })),
            }),
        }),
    }),
});
const ciscoSerialNumberResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        SystemUnit: z.object({
            Hardware: z.object({
                Module: z.object({
                    SerialNumber: z.object({
                        Value: z.string(),
                    }),
                }),
            }),
        }),
    }),
});
const ciscoSoftwareDisplayNameResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        SystemUnit: z.object({
            Software: z.object({
                DisplayName: z.object({
                    Value: z.string(),
                }),
            }),
        }),
    }),
});
const ciscoSpeakerTrackAvailabilityResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Cameras: z.object({
            SpeakerTrack: z.object({
                Availability: z.object({
                    Value: z.string(),
                }),
            }),
        }),
    }),
});
const ciscoSpeakerTrackStatusResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Cameras: z.object({
            SpeakerTrack: z.object({
                Status: z.object({
                    Value: z.string(),
                }),
            }),
        }),
    }),
});
const ciscoStandbyResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Standby: z.object({
            State: z.object({
                Value: z.string(),
            }),
        }),
    }),
});
const ciscoVideoSelfviewResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Video: z.object({
            Selfview: z.object({
                FullscreenMode: z
                    .object({
                    Value: z.string(),
                })
                    .optional(),
                Mode: z
                    .object({
                    Value: z.string(),
                })
                    .optional(),
                OnMonitorRole: z
                    .object({
                    Value: z.string(),
                })
                    .optional(),
                PIPPosition: z
                    .object({
                    Value: z.string(),
                })
                    .optional(),
            }),
        }),
    }),
});
const ciscoVolumeResp = z.object({
    ResultId: z.string().optional(),
    Status: z.object({
        Audio: z.object({
            Volume: z.object({
                Value: z.string(),
            }),
        }),
    }),
});
function isCiscoCameraConfig(config) {
    return typeof config === 'object' && 'ciscoId' in config && typeof config.ciscoId === 'string';
}
function isCiscoContactClosureConfig(config) {
    return 'pin' in config && typeof config.pin === 'number';
}
function isResp(schema, resp) {
    return schema.passthrough().safeParse(resp).success;
}
const pipPositions = {
    centerLeft: 'CenterLeft',
    centerRight: 'CenterRight',
    lowerLeft: 'LowerLeft',
    lowerRight: 'LowerRight',
    upperCenter: 'UpperCenter',
    upperLeft: 'UpperLeft',
    upperRight: 'UpperRight',
};
export class CiscoTelepresence {
    constructor(socket, state, log) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.audioDialingEnabled = true;
        this.cameraMoveDirection = {};
        this.camerasConfig = [];
        this.cmdId = 0;
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.cameras = {
            type: 'readOnlyCollectionControl',
            get: (config, tags) => __awaiter(this, void 0, void 0, function* () {
                this.camerasResp = yield this.sendCommand(`xStatus Cameras Camera`, ciscoCamerasResp, tags, { retryAttempts: 3 } // RS-232 can drop bytes
                );
                this.speakerTrackAvailabilityResp = yield this.sendCommand(`xStatus Cameras SpeakerTrack Availability`, ciscoSpeakerTrackAvailabilityResp, tags, { retryAttempts: 3 } // RS-232 can drop bytes
                );
                const selfViewResp = yield this.sendCommand(`xStatus Video Selfview`, ciscoVideoSelfviewResp, tags, { retryAttempts: 3 } // RS-232 can drop bytes
                );
                this.updateSelfView(selfViewResp); // updates selfView states
                return this.parseCameras(this.camerasResp, this.speakerTrackAvailabilityResp);
            }),
            getKey: (config) => {
                if (!isCiscoCameraConfig(config)) {
                    throw new Error(`invalid camera config: ${JSON.stringify(config)}`);
                }
                return config.ciscoId;
            },
            asyncHandlers: [
                {
                    match: resp => isResp(ciscoCamerasResp, resp) && resp.ResultId === undefined
                        ? resp
                        : undefined,
                    callback: (resp) => {
                        this.camerasResp = resp;
                        this.state.cameras.update(this.parseCameras(this.camerasResp, this.speakerTrackAvailabilityResp));
                    },
                },
                {
                    match: resp => isResp(ciscoSpeakerTrackAvailabilityResp, resp) && resp.ResultId === undefined
                        ? resp
                        : undefined,
                    callback: (resp) => {
                        this.speakerTrackAvailabilityResp = resp;
                        this.state.cameras.update(this.parseCameras(this.camerasResp, this.speakerTrackAvailabilityResp));
                    },
                },
                {
                    match: resp => isResp(ciscoVideoSelfviewResp, resp) && resp.ResultId === undefined
                        ? resp
                        : undefined,
                    callback: (resp) => {
                        this.updateSelfView(resp);
                    },
                },
            ],
        };
        this.cameraMove = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (value.action === 'repeat')
                    return value;
                for (const [key, moveDirection] of Object.entries(this.cameraMoveDirection)) {
                    const info = cameraDirectionMap[moveDirection];
                    this.sendCommand(`xCommand Camera Ramp CameraId: ${key} ${info.axis}: Stop`, ciscoGenericResp, tags);
                }
                this.cameraMoveDirection = {};
                if (value.action === 'start') {
                    const info = cameraDirectionMap[value.direction];
                    this.cameraMoveDirection[key] = value.direction;
                    this.sendCommand(`xCommand Camera Ramp CameraId: ${key} ${info.axis}: ${info.direction}`, ciscoGenericResp, tags);
                }
                return value;
            }),
        };
        this.cameraPreset = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.cameraPreset.value;
            }),
            set: (presetIndex, tags) => __awaiter(this, void 0, void 0, function* () {
                if (!this.state.power.value)
                    yield this.power.set(true, ['set.power']);
                yield this.sendCommand(`xCommand Camera Preset Activate PresetId: ${presetIndex}`, ciscoGenericResp);
                return presetIndex;
            }),
        };
        this.cameraSelected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (this.state.cameraSelected.value !== undefined) {
                    return this.state.cameraSelected.value;
                }
                return Object.keys((_a = this.state.cameras.value) !== null && _a !== void 0 ? _a : {}).sort()[0];
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c;
                if (!this.state.power.value)
                    yield this.power.set(true, ['set.power']);
                const connector = (_c = (_b = this.camerasResp) === null || _b === void 0 ? void 0 : _b.Status.Cameras.Camera.find(c => c.id === value)) === null || _c === void 0 ? void 0 : _c.DetectedConnector.Value;
                if (connector === undefined) {
                    this.log.warning(`no connector found for camera "${value}"`);
                }
                else {
                    yield this.sendCommand(`xCommand Video Input SetMainVideoSource ConnectorId: ${connector}`, ciscoGenericResp, tags);
                }
                return value;
            }),
        };
        this.cameraSelfView = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.cameraSelfView.value; // queried in cameras.get
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                const pos = this.state.cameraSelfViewPosition.value;
                const mode = value ? 'On' : 'Off';
                if (pos === 'fullscreen') {
                    yield this.sendCommand(`xCommand Video Selfview Set Mode: ${mode} FullscreenMode: On`, ciscoGenericResp);
                }
                else {
                    const pip = pipPositions[pos];
                    if (pip === undefined) {
                        this.log.error(`no pip mapping for position "${pos}"`);
                    }
                    else {
                        yield this.sendCommand(`xCommand Video Selfview Set Mode: ${mode} FullscreenMode: Off PIPPosition: ${pip}`, ciscoGenericResp);
                    }
                }
                return value;
            }),
        };
        this.cameraSelfViewPosition = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.cameraSelfViewPosition.value; // queried in cameras.get
            }),
            set: (position, tags) => __awaiter(this, void 0, void 0, function* () {
                const mode = this.state.cameraSelfView.value ? 'On' : 'Off';
                if (position === 'fullscreen') {
                    yield this.sendCommand(`xCommand Video Selfview Set Mode: ${mode} FullscreenMode: On`, ciscoGenericResp);
                }
                else {
                    const pip = pipPositions[position];
                    if (pip === undefined) {
                        this.log.error(`no pip mapping for position "${position}"`);
                    }
                    else {
                        yield this.sendCommand(`xCommand Video Selfview Set Mode: ${mode} FullscreenMode: Off PIPPosition: ${pip}`, ciscoGenericResp);
                    }
                }
                return position;
            }),
        };
        this.cameraSelfViewPositions = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return [
                    'centerLeft',
                    'centerRight',
                    'fullscreen',
                    'lowerLeft',
                    'lowerRight',
                    'upperCenter',
                    'upperLeft',
                    'upperRight',
                ];
            }),
        };
        this.cameraTracking = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.sendCommand(`xStatus Cameras SpeakerTrack Status`, ciscoSpeakerTrackStatusResp, tags, { retryAttempts: 3 } // RS-232 can drop bytes
                );
                return (resp === null || resp === void 0 ? void 0 : resp.Status.Cameras.SpeakerTrack.Status.Value) === 'Active';
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendCommand(`xCommand Cameras SpeakerTrack ${value ? 'Activate' : 'Deactivate'}`, ciscoGenericResp, tags);
                return value;
            }),
            asyncHandlers: {
                match: resp => isResp(ciscoSpeakerTrackStatusResp, resp) && resp.ResultId === undefined
                    ? resp
                    : undefined,
                callback: (resp) => {
                    const value = resp.Status.Cameras.SpeakerTrack.Status.Value === 'Active';
                    for (const key of Object.keys(this.state.cameraTracking.values)) {
                        this.state.cameraTracking.update(key, value);
                    }
                },
            },
        };
        this.contactClosure = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _d, _e;
                const resp = yield this.sendCommand(`xStatus GPIO Pin ${key} State`, ciscoGpioStateResp, tags, { retryAttempts: 3 } // RS-232 can drop bytes
                );
                return ((_e = (_d = resp === null || resp === void 0 ? void 0 : resp.Status.GPIO.Pin[0]) === null || _d === void 0 ? void 0 : _d.State.Value) !== null && _e !== void 0 ? _e : 'Low') === 'High';
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendCommand(`xCommand GPIO ManualState Set Pin${key}: ${value ? 'High' : 'Low'}`, ciscoGenericResp, tags);
                return value;
            }),
            getKey: (config) => {
                if (!isCiscoContactClosureConfig(config)) {
                    throw new Error(`invalid contact closure config: ${JSON.stringify(config)}`);
                }
                return config.pin.toString();
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!isCiscoContactClosureConfig(config)) {
                    this.log.error(`invalid contact closure config: ${JSON.stringify(config)}`);
                    return { pin: 'missing' };
                }
                if (config.pin < 1 || config.pin > 4)
                    return { pin: 'range' };
                return { pin: 'ok' };
            }),
            asyncHandlers: {
                match: resp => isResp(ciscoGpioStateResp, resp) && resp.ResultId === undefined ? resp : undefined,
                callback: (resp) => {
                    const configuredKeys = Object.keys(this.state.contactClosures.values);
                    for (const pin of resp.Status.GPIO.Pin) {
                        const key = pin.id;
                        if (!(key in configuredKeys))
                            continue;
                        const value = pin.State.Value === 'High';
                        this.state.contactClosures.update(key, value);
                    }
                },
            },
        };
        this.contentSource = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _f, _g;
                const resp = yield this.sendCommand(`xStatus Conference Presentation LocalInstance`, ciscoPresentationLocalInstanceResp, tags, { allowError: true } // error is okay
                );
                return ((_g = (_f = resp === null || resp === void 0 ? void 0 : resp.Status.Conference.Presentation.LocalInstance[0].Source) === null || _f === void 0 ? void 0 : _f.Value) !== null && _g !== void 0 ? _g : 0).toString();
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                if (code === this.state.contentSource.value)
                    return code;
                if (code === '0') {
                    yield this.sendCommand(`xCommand Presentation Stop`, ciscoGenericResp, tags);
                }
                else {
                    yield this.sendCommand(`xCommand Presentation Start PresentationSource: ${code}`, ciscoGenericResp, tags);
                }
                return code;
            }),
            asyncHandlers: {
                match: resp => isResp(ciscoPresentationLocalInstanceResp, resp) && resp.ResultId === undefined
                    ? resp
                    : undefined,
                callback: (resp) => {
                    var _a, _b, _c;
                    const prevSource = this.state.contentSource.value;
                    const newSource = ((_c = (_b = (_a = resp.Status.Conference.Presentation.LocalInstance) === null || _a === void 0 ? void 0 : _a[0].Source) === null || _b === void 0 ? void 0 : _b.Value) !== null && _c !== void 0 ? _c : 0).toString();
                    if (newSource !== prevSource) {
                        this.state.contentState.update(prevSource, 'none');
                    }
                    this.state.contentSource.update(newSource);
                    this.state.contentState.update(newSource, newSource === '0' ? 'none' : 'sending');
                },
            },
        };
        this.contentState = {
            get: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                return code !== '0' && this.state.contentSource.value === code ? 'sending' : 'none';
            }),
        };
        this.contentStop = {
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.contentSource.set('0', [...tags, 'set.contentSource']);
                return code;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _h;
                const resp = yield this.sendCommand(`xStatus SystemUnit Software DisplayName`, ciscoSoftwareDisplayNameResp, tags, { retryAttempts: 3 } // RS-232 can drop bytes
                );
                return (_h = resp === null || resp === void 0 ? void 0 : resp.Status.SystemUnit.Software.DisplayName.Value) !== null && _h !== void 0 ? _h : 'unknown';
            }),
        };
        this.mute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.sendCommand(`xStatus Audio Microphones Mute`, ciscoMicrophoneMuteResp, tags, { retryAttempts: 3 } // RS-232 can drop bytes
                );
                return (resp === null || resp === void 0 ? void 0 : resp.Status.Audio.Microphones.Mute.Value) === 'On';
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendCommand(`xCommand Audio Microphones ${value ? 'Mute' : 'Unmute'}`, ciscoGenericResp, tags);
                return value;
            }),
            asyncHandlers: {
                match: resp => isResp(ciscoMicrophoneMuteResp, resp) && resp.ResultId === undefined
                    ? resp
                    : undefined,
                callback: (resp) => {
                    this.state.mute.update(resp.Status.Audio.Microphones.Mute.Value === 'On');
                },
            },
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.sendCommand(`xStatus Standby`, ciscoStandbyResp, tags);
                return (resp === null || resp === void 0 ? void 0 : resp.Status.Standby.State.Value) === 'Off';
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (value != this.state.power.value) {
                    this.state.busy.update(true);
                    this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                }
                yield this.sendCommand(`xCommand Standby ${value ? 'Deactivate' : 'Activate'}`, ciscoGenericResp, tags);
                return value;
            }),
            asyncHandlers: {
                match: resp => isResp(ciscoStandbyResp, resp) && resp.ResultId === undefined ? resp : undefined,
                callback: (resp) => {
                    const newPower = resp.Status.Standby.State.Value === 'Off';
                    if (newPower != this.state.power.value) {
                        this.state.power.update(newPower);
                        this.state.busy.update(true);
                        this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                    }
                },
            },
        };
        this.reboot = {
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendCommand(`xCommand SystemUnit Boot Action: Restart`, ciscoGenericResp, tags);
            }),
        };
        this.serialNumber = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _j;
                const resp = yield this.sendCommand(`xStatus SystemUnit Hardware Module SerialNumber`, ciscoSerialNumberResp, tags, { retryAttempts: 3 } // RS-232 can drop bytes
                );
                return (_j = resp === null || resp === void 0 ? void 0 : resp.Status.SystemUnit.Hardware.Module.SerialNumber.Value) !== null && _j !== void 0 ? _j : 'unknown';
            }),
        };
        this.telephony = {
            answer: (key) => __awaiter(this, void 0, void 0, function* () {
                const tags = [`answer.${key}`];
                yield this.sendCommand(`xCommand Call Accept CallId: ${key}`, ciscoCallAcceptResp, tags);
                return false;
            }),
            call: (value) => __awaiter(this, void 0, void 0, function* () {
                var _k, _l, _m;
                const callType = value.callType === 'voip' ? 'Audio' : 'Video';
                // Set outgoingCallExtras before sending the dial command. This is so that if we get an
                // async response first, then the outgoingCallExtras will get added to it.
                if (value.callType === 'voip') {
                    this.outgoingCallExtras = {
                        dialString: value.dialString,
                    };
                    if (value.meetingCodes) {
                        this.outgoingCallExtras.meetingCodes = [...value.meetingCodes];
                    }
                }
                // Send the dial command.
                const resp = yield this.sendCommand(`xCommand Dial Number: "${value.dialString}" CallType: ${callType}`, ciscoDialResp);
                const callId = (_m = (_l = (_k = resp === null || resp === void 0 ? void 0 : resp.CommandResponse) === null || _k === void 0 ? void 0 : _k.DialResult) === null || _l === void 0 ? void 0 : _l.CallId) === null || _m === void 0 ? void 0 : _m.Value;
                if (callId !== undefined) {
                    if (Object.keys(this.state.calls.values).includes(callId)) {
                        // We could receive an async call status update before we receive the OK
                        // response to the dial command. In that case, the call already exists, so just
                        // update the call info.
                        this.state.calls.updateCall(callId, value);
                        this.outgoingCallExtras = undefined;
                        return undefined;
                    }
                    else {
                        // We haven't received an async call status update yet, so return the call ID
                        // and let the framework add a new call with the dial string.
                        this.outgoingCallExtras = undefined;
                        return callId;
                    }
                }
                return undefined;
            }),
            dial: (key, value) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendCommand(`xCommand Call DTMFSend CallId: ${key} DTMFString: "${value}"`, ciscoGenericResp);
                return value;
            }),
            getCalls: () => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.sendCommand(`xStatus Call`, ciscoCallQueryResp, ['get.calls'], { retryAttempts: 3 } // RS-232 can drop bytes
                );
                return Object.fromEntries(Object.entries(this.parseCalls(resp)).map(([key, call]) => [
                    key,
                    this.state.calls.addMissing(call),
                ]));
            }),
            getMaxCalls: () => __awaiter(this, void 0, void 0, function* () {
                var _o, _p, _q, _r, _s, _t;
                let maxAudioCalls = parseInt((_q = (_p = (_o = (yield this.sendCommand(`xStatus Capabilities Conference MaxAudioCalls`, ciscoMaxAudioCallsResp, ['get.maxAudioCalls']))) === null || _o === void 0 ? void 0 : _o.Status.Capabilities) === null || _p === void 0 ? void 0 : _p.Conference.MaxAudioCalls.Value) !== null && _q !== void 0 ? _q : '0', 10);
                if (Number.isNaN(maxAudioCalls)) {
                    this.log.error(`invalid max audio calls`);
                    maxAudioCalls = 0;
                }
                if (!this.audioDialingEnabled)
                    maxAudioCalls = 0;
                let maxVideoCalls = parseInt((_t = (_s = (_r = (yield this.sendCommand(`xStatus Capabilities Conference MaxVideoCalls`, ciscoMaxVideoCallsResp, ['get.maxVideoCalls']))) === null || _r === void 0 ? void 0 : _r.Status.Capabilities) === null || _s === void 0 ? void 0 : _s.Conference.MaxVideoCalls.Value) !== null && _t !== void 0 ? _t : '0', 10);
                if (Number.isNaN(maxVideoCalls)) {
                    this.log.error(`invalid max video calls`);
                    maxVideoCalls = 0;
                }
                return {
                    video: maxVideoCalls,
                    voip: maxAudioCalls,
                };
            }),
            hangup: (key) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendCommand(`xCommand Call Disconnect CallId: ${key}`, ciscoCallDisconnectResp);
                return false;
            }),
            reject: (key) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendCommand(`xCommand Call Reject CallId: ${key}`, ciscoCallRejectResp);
                return false;
            }),
            asyncHandlers: {
                match: resp => (isResp(ciscoCallQueryResp, resp) && resp.ResultId === 'aveoCallsPoll') ||
                    (isResp(ciscoCallAsyncResp, resp) && resp.ResultId === undefined)
                    ? resp
                    : undefined,
                callback: (resp) => {
                    var _a;
                    const calls = this.parseCalls(resp);
                    for (const [key, call] of Object.entries(calls)) {
                        const current = this.state.calls.values[key];
                        if (current === undefined) {
                            if (call.callState !== 'disconnected') {
                                if (call.callType === 'voip' && this.outgoingCallExtras) {
                                    const xcall = Object.assign(Object.assign({}, this.outgoingCallExtras), call);
                                    this.state.calls.update(key, this.state.calls.addMissing(xcall));
                                    this.outgoingCallExtras = undefined;
                                }
                                else {
                                    this.state.calls.update(key, this.state.calls.addMissing(call));
                                }
                            }
                        }
                        else {
                            this.state.calls.updateExisting(key, Object.assign(Object.assign({}, current), call));
                        }
                    }
                    for (const key of Object.keys(this.state.calls.values)) {
                        if (calls[key] === undefined) {
                            this.state.calls.disconnect(key);
                        }
                    }
                    for (const [key, call] of Object.entries(this.state.calls.values)) {
                        if (call.callState === 'disconnected' &&
                            ((_a = call.end) !== null && _a !== void 0 ? _a : 0) < Date.now() - 60 * 60 * 1000) {
                            this.state.calls.remove(key);
                        }
                    }
                },
            },
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _u;
                const resp = yield this.sendCommand(`xStatus Audio Volume`, ciscoVolumeResp, tags, { retryAttempts: 3 } // RS-232 can drop bytes
                );
                let volume = parseInt((_u = resp === null || resp === void 0 ? void 0 : resp.Status.Audio.Volume.Value) !== null && _u !== void 0 ? _u : '0', 10);
                if (Number.isNaN(volume)) {
                    this.log.error(`invalid volume value`);
                    volume = 0;
                }
                return volume;
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendCommand(`xCommand Audio Volume Set Level: ${value}`, ciscoGenericResp, tags);
                return value;
            }),
            range: { max: 100, min: 0, round: 1 },
            asyncHandlers: {
                match: resp => isResp(ciscoVolumeResp, resp) && resp.ResultId === undefined ? resp : undefined,
                callback: (resp) => {
                    var _a;
                    const volume = parseInt((_a = resp.Status.Audio.Volume.Value) !== null && _a !== void 0 ? _a : '0', 10);
                    if (Number.isNaN(volume)) {
                        this.log.error(`invalid volume value`);
                        return;
                    }
                    this.state.volume.update(volume);
                },
            },
        };
        this.volumeMute = {
            min: 0,
        };
        // polling
        this.polls = [
            {
                function: () => __awaiter(this, void 0, void 0, function* () {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const serialNumber = window.inaraSerialNumber;
                    yield this.sendCommand(`xCommand Peripherals Heartbeat ID: "${serialNumber}"`, ciscoGenericResp, ['poll']);
                }),
                intervalMs: 4000,
            },
            {
                function: () => __awaiter(this, void 0, void 0, function* () {
                    // Poll calls occasionally just in case the codec forgets to send us an update via
                    // the calls feedback registration. We send a calls query and let telephony async
                    // handler process the response.
                    yield this.socket.sendAsync(`xStatus Call | resultId=aveoCallsPoll`, [
                        'poll',
                        'get.calls',
                    ]);
                }),
                intervalMs: 10000,
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.audioDialingEnabled = (_a = config.audioDialingEnabled) !== null && _a !== void 0 ? _a : true;
            this.outgoingCallExtras = undefined;
            this.camerasConfig.length = 0;
            for (const cameraConfig of Object.values((_b = config.cameras) !== null && _b !== void 0 ? _b : {})) {
                if (isCiscoCameraConfig(cameraConfig)) {
                    this.camerasConfig.push(cameraConfig);
                }
                else {
                    this.log.error(`invalid camera config: ${JSON.stringify(cameraConfig)}`);
                }
            }
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            this.outgoingCallExtras = undefined;
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials, config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const tags = ['initializing'];
            if (this.socket.port === 4999 && credentials.username !== undefined) {
                // Blindly send login credentials because making the unterminated login propmts play
                // nice with the JSON parser is ugly.
                yield this.timeouts.delay(2000);
                this.socket.sendAsync(credentials.username);
                yield this.timeouts.delay(2000);
                this.socket.sendAsync((_a = credentials.password) !== null && _a !== void 0 ? _a : '');
            }
            this.socket.sendAsync(`echo off`);
            this.socket.sendAsync(`xPreferences outputmode json`);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const softwareVersion = window.inaraVersion;
            const serialNumber = window.inaraSerialNumber;
            try {
                yield this.sendCommand(`xCommand Peripherals Connect` +
                    ` ID: "${serialNumber}"` +
                    ` Name: "Mira Connect"` +
                    ` SerialNumber: "${serialNumber}"` +
                    ` SoftwareInfo: "${softwareVersion}"` +
                    ` Type: "ControlSystem"`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback deregisterall`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback register /Status/Standby`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback register /Status/Audio/Microphones/Mute`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback register /Status/Audio/Volume`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback register /Status/Call`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback register /Status/Cameras`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback register /Status/Cameras/SpeakerTrack/Availability`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback register /Status/Cameras/SpeakerTrack/Status`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback register /Status/Video/Selfview`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback register /Status/Conference/Presentation/LocalInstance`, ciscoGenericResp, tags);
                yield this.sendCommand(`xFeedback register /Status/GPIO/Pin`, ciscoGenericResp, tags);
            }
            catch (error) {
                return 'verifyFailed';
            }
            return 'connected';
        });
    }
    // other class methods
    parseCalls(resp) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        const calls = {};
        for (const call of (_b = (_a = resp === null || resp === void 0 ? void 0 : resp.Status) === null || _a === void 0 ? void 0 : _a.Call) !== null && _b !== void 0 ? _b : []) {
            const callId = call.id;
            if (call.ghost === 'True') {
                const call = this.state.calls.values[callId];
                if (call !== undefined) {
                    calls[callId] = Object.assign(Object.assign({}, call), { callState: 'disconnected' });
                }
                continue;
            }
            const newCall = {};
            if (((_c = call.CallType) === null || _c === void 0 ? void 0 : _c.Value) !== undefined) {
                newCall.callType = call.CallType.Value.startsWith('Audio') ? 'voip' : 'video';
            }
            if (((_d = call.Direction) === null || _d === void 0 ? void 0 : _d.Value) !== undefined) {
                newCall.direction = call.Direction.Value === 'Incoming' ? 'incoming' : 'outgoing';
            }
            if (((_e = this.state.calls.values[callId]) === null || _e === void 0 ? void 0 : _e.start) === undefined &&
                call.Duration !== undefined) {
                const duration = parseInt(call.Duration.Value, 10);
                if (!Number.isNaN(duration)) {
                    newCall.start = Date.now() - duration * 1000;
                }
                else {
                    this.log.error(`invalid call duration`);
                }
            }
            if (((_f = this.state.calls.values[callId]) === null || _f === void 0 ? void 0 : _f.dialString) === undefined &&
                ((_g = call.CallbackNumber) === null || _g === void 0 ? void 0 : _g.Value) !== undefined) {
                newCall.dialString = call.CallbackNumber.Value.replace(/^[^ ]+:/, '');
            }
            if (((_h = call.DisplayName) === null || _h === void 0 ? void 0 : _h.Value) !== undefined) {
                newCall.contactName = (_j = call.DisplayName) === null || _j === void 0 ? void 0 : _j.Value;
            }
            //if (call.RemoteNumber?.Value !== undefined) {
            //    newCall.displayDialString = call.RemoteNumber.Value;
            //}
            if (((_k = call.Status) === null || _k === void 0 ? void 0 : _k.Value) === undefined) {
                const callState = (_l = this.state.calls.values[callId]) === null || _l === void 0 ? void 0 : _l.callState;
                if (callState !== undefined) {
                    newCall.callState = callState;
                }
                else {
                    this.log.error(`missing call state for call ${callId}`);
                }
            }
            else {
                switch ((_m = call.Status) === null || _m === void 0 ? void 0 : _m.Value) {
                    case 'Connecting':
                    case 'Dialling': // typo in Cisco API
                    case 'Dialing': // in case they fix it one day
                    case 'EarlyMedia':
                    case 'Ringing':
                        newCall.callState = 'ringing';
                        break;
                    case 'Connected':
                    case 'Answered':
                    case 'OnHold':
                    case 'Preserved':
                    case 'RemotePreserved':
                        newCall.callState = 'connected';
                        break;
                    case 'Disconnecting':
                        newCall.callState = 'disconnecting';
                        break;
                    case 'Idle':
                        newCall.callState = 'disconnected';
                        break;
                    default:
                        this.log.error(`unhandled call status: ${(_o = call.Status) === null || _o === void 0 ? void 0 : _o.Value}`);
                        break;
                }
            }
            newCall.canDial = newCall.callState === 'connected';
            calls[callId] = newCall;
        }
        return calls;
    }
    parseCameras(camerasResp, speakerTrackAvailabilityResp) {
        var _a, _b, _c, _d, _e, _f;
        const cameras = {};
        const hasCameraTracking = ((_a = speakerTrackAvailabilityResp === null || speakerTrackAvailabilityResp === void 0 ? void 0 : speakerTrackAvailabilityResp.Status.Cameras.SpeakerTrack.Availability.Value) !== null && _a !== void 0 ? _a : 'Unavailable') === 'Available';
        for (const camera of (_b = camerasResp === null || camerasResp === void 0 ? void 0 : camerasResp.Status.Cameras.Camera) !== null && _b !== void 0 ? _b : []) {
            const cameraId = camera.id;
            const hasPtz = ((_e = (_d = (_c = camera.Capabilities) === null || _c === void 0 ? void 0 : _c.Options) === null || _d === void 0 ? void 0 : _d.Value) !== null && _e !== void 0 ? _e : '').includes('ptz');
            const config = this.camerasConfig.find(c => c.ciscoId === cameraId);
            cameras[cameraId] = {
                name: (_f = config === null || config === void 0 ? void 0 : config.name) !== null && _f !== void 0 ? _f : `Camera ${camera.id}`,
                hasCameraTracking,
                hasPan: hasPtz,
                hasTilt: hasPtz,
                hasZoom: hasPtz,
                location: 'near',
            };
        }
        return cameras;
    }
    sendCommand(cmd, schema, tags, options) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const id = `aveo${(this.cmdId++).toString().padStart(3, '0')}`;
            if (this.cmdId > 999)
                this.cmdId = 0;
            const resp = yield this.socket.send(`${cmd} | resultId="${id}"`, resp => typeof resp === 'object' &&
                resp !== null &&
                'ResultId' in resp &&
                resp.ResultId === id
                ? resp
                : undefined, tags !== null && tags !== void 0 ? tags : [], options);
            const parsedResponse = resp !== undefined && isResp(schema, resp) ? resp : undefined;
            if (parsedResponse === undefined && !((_a = options === null || options === void 0 ? void 0 : options.allowError) !== null && _a !== void 0 ? _a : false)) {
                this.log.error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return parsedResponse;
        });
    }
    updateSelfView(resp) {
        var _a, _b, _c, _d;
        const selfview = (_b = (_a = resp === null || resp === void 0 ? void 0 : resp.Status) === null || _a === void 0 ? void 0 : _a.Video) === null || _b === void 0 ? void 0 : _b.Selfview;
        if (selfview === undefined)
            return;
        if (selfview.Mode !== undefined) {
            this.state.cameraSelfView.update(selfview.Mode.Value === 'On');
        }
        if (((_c = selfview.FullscreenMode) === null || _c === void 0 ? void 0 : _c.Value) === 'On') {
            this.state.cameraSelfViewPosition.update('fullscreen');
        }
        else {
            let position;
            for (const [pos, pip] of Object.entries(pipPositions)) {
                if (pip === ((_d = selfview.PIPPosition) === null || _d === void 0 ? void 0 : _d.Value)) {
                    position = pos;
                    break;
                }
            }
            if (position !== undefined) {
                this.state.cameraSelfViewPosition.update(position);
            }
        }
    }
}
// static properties
CiscoTelepresence.driverType = 'socketDriver';
CiscoTelepresence.key = '-KmaYBQ2O8SUAQRQTumH';
CiscoTelepresence.baseSocketOptions = {
    commandExtractor: createJsonCommandExtractor({ nonJsonBehavior: 'extract' }),
    parser: command => jsonParser(command),
    sendTerminator: '\r\n',
};
CiscoTelepresence.socketOptions = [
    Object.assign(Object.assign({}, CiscoTelepresence.baseSocketOptions), { port: 22, socketType: 'ssh' }),
    Object.assign(Object.assign({}, CiscoTelepresence.baseSocketOptions), { port: 4999 }),
];
